import { Box, Button, Flex, IconButton, Input, Select, SimpleGrid, Text } from "@chakra-ui/react";
import { FaPlus, FaTrash } from "react-icons/fa";
import { crm } from "../../../theme";
import { RegAddress, RegDocument, RegDomainOption, RegDomains, RegItemSettings } from "../../../services/govRegistrationService";

const labelProps = { fontSize: "12px", fontWeight: "600", color: crm.textMuted, mb: "4px" };
const inputProps = { h: "38px", borderRadius: "10px", fontSize: "13px", focusBorderColor: crm.brand };

export const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Box>
    <Text {...labelProps}>{label}</Text>
    {children}
  </Box>
);

const optLabel = (opts: RegDomainOption[], code?: string | null) => opts.find((o) => o.code === code)?.label;

const DomainSelect = ({
  label,
  value,
  options,
  inherit,
  onChange,
}: {
  label: string;
  value?: string | null;
  options: RegDomainOption[];
  inherit?: string | null;
  onChange: (v: string | null) => void;
}) => (
  <Field label={label}>
    <Select {...inputProps} value={value ?? ""} onChange={(e) => onChange(e.target.value || null)}>
      <option value="">{inherit !== undefined ? `Padrão: ${optLabel(options, inherit) ?? "do sistema"}` : "Padrão do sistema"}</option>
      {options.map((o) => (
        <option key={o.code} value={o.code}>
          {o.label}
        </option>
      ))}
    </Select>
  </Field>
);

const NumberInput = ({
  label,
  value,
  placeholder,
  onChange,
}: {
  label: string;
  value?: number | null;
  placeholder?: string;
  onChange: (v: number | null) => void;
}) => (
  <Field label={label}>
    <Input
      {...inputProps}
      type="number"
      value={value ?? ""}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
    />
  </Field>
);

const TextInput = ({
  label,
  value,
  placeholder,
  maxLength,
  onChange,
}: {
  label: string;
  value?: string | null;
  placeholder?: string;
  maxLength?: number;
  onChange: (v: string | null) => void;
}) => (
  <Field label={label}>
    <Input {...inputProps} value={value ?? ""} placeholder={placeholder} maxLength={maxLength} onChange={(e) => onChange(e.target.value || null)} />
  </Field>
);

export const AddressFields = ({ value, onChange }: { value?: RegAddress | null; onChange: (v: RegAddress) => void }) => {
  const a = value ?? {};
  const set = (patch: Partial<RegAddress>) => onChange({ ...a, ...patch });
  return (
    <SimpleGrid columns={{ base: 1, md: 3 }} spacing="12px">
      <TextInput label="Logradouro" value={a.logradouro} maxLength={70} onChange={(v) => set({ logradouro: v })} />
      <TextInput label="Cidade" value={a.nomeCidade} maxLength={35} onChange={(v) => set({ nomeCidade: v })} />
      <TextInput label="Código postal" value={a.cep} maxLength={9} onChange={(v) => set({ cep: v })} />
      <TextInput label="Subdivisão do país (AA-AAA)" value={a.codigoSubdivisaoPais} maxLength={6} onChange={(v) => set({ codigoSubdivisaoPais: v })} />
      <TextInput label="TIN (identificação fiscal)" value={a.tin} maxLength={35} onChange={(v) => set({ tin: v })} />
      <TextInput label="E-mail" value={a.email} maxLength={70} onChange={(v) => set({ email: v })} />
    </SimpleGrid>
  );
};

/** Campos de item. `inherit` = valores herdados (mostrados na opção "Padrão: …" de cada select). */
export const ItemSettingsFields = ({
  value,
  domains,
  inherit,
  onChange,
}: {
  value: RegItemSettings;
  domains: RegDomains;
  inherit?: RegItemSettings;
  onChange: (v: RegItemSettings) => void;
}) => {
  const set = (patch: Partial<RegItemSettings>) => onChange({ ...value, ...patch });
  const cov = value.coberturaCambial ?? inherit?.coberturaCambial ?? "ATE_180_DIAS";
  const different = (value.indicadorExportadorFabricante ?? inherit?.indicadorExportadorFabricante) === "EXPORTADOR_DIFERENTE_FABRICANTE";
  return (
    <Box>
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing="12px">
        <DomainSelect label="Tipo de aplicação" value={value.tipoAplicacao} options={domains.tipoAplicacao} inherit={inherit ? inherit.tipoAplicacao : undefined} onChange={(v) => set({ tipoAplicacao: v })} />
        <DomainSelect label="Condição da mercadoria" value={value.condicao} options={domains.condicao} inherit={inherit ? inherit.condicao : undefined} onChange={(v) => set({ condicao: v })} />
        <DomainSelect label="Vinculação comprador/vendedor" value={value.indicadorCompradorVendedor} options={domains.compradorVendedor} inherit={inherit ? inherit.indicadorCompradorVendedor : undefined} onChange={(v) => set({ indicadorCompradorVendedor: v })} />
        <DomainSelect label="Exportador x fabricante" value={value.indicadorExportadorFabricante} options={domains.exportadorFabricante} inherit={inherit ? inherit.indicadorExportadorFabricante : undefined} onChange={(v) => set({ indicadorExportadorFabricante: v })} />
        <NumberInput label="Método de valoração (código)" value={value.metodoValoracao} placeholder={inherit?.metodoValoracao != null ? `Padrão: ${inherit.metodoValoracao}` : "1"} onChange={(v) => set({ metodoValoracao: v })} />
        <DomainSelect label="Cobertura cambial" value={value.coberturaCambial} options={domains.cobertura} inherit={inherit ? inherit.coberturaCambial : undefined} onChange={(v) => set({ coberturaCambial: v })} />
        {cov === "SEM_COBERTURA" && (
          <NumberInput label="Motivo sem cobertura (código Siscomex)" value={value.motivoSemCobertura} placeholder={inherit?.motivoSemCobertura != null ? `Padrão: ${inherit.motivoSemCobertura}` : "ex.: 52"} onChange={(v) => set({ motivoSemCobertura: v })} />
        )}
        {cov === "ACIMA_360" && (
          <NumberInput label="Instituição financiadora (código)" value={value.instituicaoFinanciadora} placeholder={inherit?.instituicaoFinanciadora != null ? `Padrão: ${inherit.instituicaoFinanciadora}` : "1 a 99"} onChange={(v) => set({ instituicaoFinanciadora: v })} />
        )}
        {(cov === "DE_180_ATE_360" || cov === "ACIMA_360") && (
          <>
            <TextInput label="Número do ROF" value={value.numeroRof} onChange={(v) => set({ numeroRof: v })} />
            <TextInput label="Valor da cobertura cambial" value={value.valorCobertura} onChange={(v) => set({ valorCobertura: v })} />
          </>
        )}
      </SimpleGrid>
      {different && (
        <Box mt="12px" p="12px" bg={crm.subtle} border={`1px solid ${crm.border}`} borderRadius={crm.radiusSm}>
          <Text fontSize="12px" color={crm.textMuted} mb="8px">
            Fabricante diferente do exportador: será cadastrado como operador estrangeiro no Catálogo ao transmitir.
          </Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing="12px">
            <TextInput label="Nome do fabricante" value={value.manufacturerName ?? inherit?.manufacturerName} maxLength={150} onChange={(v) => set({ manufacturerName: v })} />
            <TextInput label="País do fabricante" value={value.manufacturerCountry ?? inherit?.manufacturerCountry} placeholder="nome ou ISO (ex.: Vietnã, VN)" onChange={(v) => set({ manufacturerCountry: v })} />
          </SimpleGrid>
          <Text {...labelProps} mt="12px">
            Endereço do fabricante
          </Text>
          <AddressFields value={value.manufacturerAddress ?? inherit?.manufacturerAddress} onChange={(a) => set({ manufacturerAddress: a })} />
        </Box>
      )}
    </Box>
  );
};

/** Lista editável de documentos de instrução (tipo + palavras-chave). */
export const DocumentsEditor = ({ value, onChange, hint }: { value: RegDocument[]; onChange: (v: RegDocument[]) => void; hint?: string }) => {
  const setDoc = (i: number, d: RegDocument) => onChange(value.map((x, k) => (k === i ? d : x)));
  return (
    <Box>
      {hint && (
        <Text fontSize="12px" color={crm.textFaint} mb="8px">
          {hint}
        </Text>
      )}
      {value.map((d, i) => (
        <Box key={i} mb="10px" p="10px" border={`1px solid ${crm.border}`} borderRadius={crm.radiusSm}>
          <Flex gap="8px" align="flex-end">
            <Box flex="1">
              <TextInput label="Tipo do documento (código e-Docex)" value={d.type} onChange={(v) => setDoc(i, { ...d, type: v ?? "" })} />
            </Box>
            <IconButton aria-label="Remover documento" size="sm" variant="ghost" icon={<FaTrash />} onClick={() => onChange(value.filter((_, k) => k !== i))} />
          </Flex>
          {d.keywords.map((k, j) => (
            <Flex key={j} gap="8px" mt="8px" align="flex-end">
              <Box w="120px">
                <NumberInput label="Palavra-chave (código)" value={k.code || null} onChange={(v) => setDoc(i, { ...d, keywords: d.keywords.map((x, m) => (m === j ? { ...x, code: v ?? 0 } : x)) })} />
              </Box>
              <Box flex="1">
                <TextInput label="Valor" value={k.value} onChange={(v) => setDoc(i, { ...d, keywords: d.keywords.map((x, m) => (m === j ? { ...x, value: v ?? "" } : x)) })} />
              </Box>
              <IconButton aria-label="Remover palavra-chave" size="sm" variant="ghost" icon={<FaTrash />} onClick={() => setDoc(i, { ...d, keywords: d.keywords.filter((_, m) => m !== j) })} />
            </Flex>
          ))}
          <Button size="xs" variant="ghost" mt="6px" leftIcon={<FaPlus />} onClick={() => setDoc(i, { ...d, keywords: [...d.keywords, { code: 0, value: "" }] })}>
            Palavra-chave
          </Button>
        </Box>
      ))}
      <Button size="xs" variant="outline" leftIcon={<FaPlus />} onClick={() => onChange([...value, { type: "", keywords: [{ code: 0, value: "" }] }])}>
        Documento
      </Button>
    </Box>
  );
};

export { TextInput };
